/**
 * Build a lookup of player initial → handicap from the group definition.
 */
export function makeHdcpLookup(group) {
    const m = new Map();
    for (const mem of group.members ?? []) {
        const key = String(mem.id ?? '').trim();
        if (!key)
            continue;
        const h = Number(mem.hdcp);
        m.set(key, Number.isFinite(h) ? h : 0);
    }
    return m;
}
/**
 * Safe handicap lookup with trimming and defaulting to 0.
 */
export function getHdcp(initial, hcpByInitial) {
    const k = (initial ?? '').trim();
    if (!k)
        return 0;
    return hcpByInitial.get(k) ?? 0;
}
/**
 * Parses a comma-separated roster CSV into a clean string[] of initials.
 */
export function parseRosterCsv(csv) {
    return String(csv ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}
export function buildRosterEntries(players, group) {
    const strokesPerHole = getStrokesPerHole();
    return (players ?? []).map((player) => {
        const memberId = String(player.memberId ?? '').trim();
        const displayCode = String(player.displayCode ?? '').trim().toUpperCase();
        const mem = (group.members ?? []).find((m) => String(m.id ?? '').trim() === memberId);
        const nickName = mem?.nickName?.trim() ||
            mem?.name?.trim() ||
            displayCode;
        return {
            id: memberId,
            handicap: Number(player.handicap),
            strokesPerHole,
            nickName,
            displayCode,
        };
    });
}
export function validateRoundPlayers(roster) {
    // displayCode (initial) uniqueness (case-insensitive)
    const codeMap = new Map();
    for (const r of roster) {
        const code = (r.displayCode ?? '').trim().toUpperCase();
        if (!code)
            continue;
        if (codeMap.has(code)) {
            const other = codeMap.get(code);
            return `Duplicate initial in playing group: "${r.displayCode}" is used by at least two players. Please adjust initials.`;
        }
        codeMap.set(code, r);
    }
    return null; // All good
}
/**
 * Validates and constructs an IRoundContext.
 */
export function makeRoundContext(sources) {
    const { holesInRound, roster, pars, handicaps } = sources;
    if (!Number.isFinite(holesInRound) || holesInRound <= 0) {
        throw new Error(`holesInRound must be a positive number (got ${holesInRound})`);
    }
    if (pars.length !== holesInRound) {
        throw new Error(`pars.length (${pars.length}) != holesInRound (${holesInRound})`);
    }
    if (handicaps.length !== holesInRound) {
        throw new Error(`handicaps.length (${handicaps.length}) != holesInRound (${holesInRound})`);
    }
    if (!Array.isArray(roster) || roster.length === 0) {
        throw new Error(`roster must be a non-empty IRosterEntry[]`);
    }
    for (let i = 0; i < roster.length; i++) {
        const r = roster[i];
        if (!r?.id || typeof r.id !== 'string') {
            throw new Error(`roster[${i}].id must be a non-empty string`);
        }
        if (!Number.isFinite(r.handicap)) {
            throw new Error(`roster[${i}].handicap must be a number`);
        }
    }
    return { holesInRound, roster, pars, handicaps };
}
function getStrokesPerHole() {
}
//# sourceMappingURL=RoundContextBuilder.js.map